import React, { useMemo, useState } from 'react'
import { Button } from '../../ui'

/**
 * Sticky bulk-action bar that appears when at least one task is selected.
 *
 * Each handler is async and returns a `{ success, failed }` summary so
 * the parent can render a single inline status line — no toast system.
 */
export function TasksBulkActionBar({
  selectedTasks,
  pairs,
  onMarkDone,
  onMarkCancelled,
  onReassign,
  onDelete,
  onClear,
}) {
  const [reassignMenuOpen, setReassignMenuOpen] = useState(false)

  // Reassign is only valid when every selected task lives in the same pair.
  const samePair = useMemo(() => {
    if (selectedTasks.length === 0) return null
    const first = selectedTasks[0]
    const key = `${first.reportId}::${first.managerId}`
    const allMatch = selectedTasks.every(
      (t) => `${t.reportId}::${t.managerId}` === key,
    )
    return allMatch ? key : null
  }, [selectedTasks])

  const reassignDisabled = !samePair
  const targetPair = samePair
    ? pairs.find((p) => p.pairId === samePair) ?? null
    : null

  if (selectedTasks.length === 0) return null

  return (
    <div role="region" aria-live="polite" aria-label="Bulk actions" style={styles.bar}>
      <span style={styles.count}>{selectedTasks.length} selected</span>
      <Button size="sm" variant="secondary" onClick={onMarkDone}>Mark done</Button>
      <Button size="sm" variant="secondary" onClick={onMarkCancelled}>Mark cancelled</Button>
      <div style={{ position: 'relative' }}>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => !reassignDisabled && setReassignMenuOpen((v) => !v)}
          disabled={reassignDisabled}
          aria-disabled={reassignDisabled || undefined}
          title={
            reassignDisabled
              ? 'Select tasks from a single pair to reassign'
              : undefined
          }
        >
          Reassign…
        </Button>
        {reassignMenuOpen && targetPair && (
          <div role="menu" style={styles.menu}>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setReassignMenuOpen(false)
                onReassign(targetPair.reportId)
              }}
              style={styles.menuItem}
            >
              {targetPair.reportName}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setReassignMenuOpen(false)
                onReassign(targetPair.managerId)
              }}
              style={styles.menuItem}
            >
              {targetPair.managerName}
            </button>
          </div>
        )}
      </div>
      <Button size="sm" variant="secondary" onClick={onDelete}>Delete</Button>
      <span style={{ marginLeft: 'auto' }}>
        <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>
      </span>
    </div>
  )
}

const styles = {
  bar: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    fontSize: 12,
    color: 'var(--text-primary)',
  },
  count: {
    fontWeight: 600,
    marginRight: 6,
  },
  // Custom dropdown menu — no Menu primitive yet, kept inline.
  menu: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
    zIndex: 10,
    minWidth: 140,
  },
  menuItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '6px 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    color: 'var(--text-primary)',
  },
}
